// React entry-point for the QueryRunner SQLite engine. Owns the worker
// lifecycle, exposes the typed client, and tracks engine init state.

import { useEffect, useRef, useState } from 'react';
import { SqliteClient } from './SqliteClient';
import type { WorkerInitInfo } from '../worker/protocol';

export type EngineStatus =
  | { phase: 'loading' }
  | { phase: 'ready'; info: WorkerInitInfo }
  | { phase: 'error'; message: string };

export function useSqlite(): {
  client: SqliteClient | null;
  status: EngineStatus;
} {
  const [status, setStatus] = useState<EngineStatus>({ phase: 'loading' });
  const clientRef = useRef<SqliteClient | null>(null);

  // External-system effect: own the worker lifecycle. The client wraps a
  // Worker (external system); we instantiate once per component mount and
  // tear it down on unmount so re-opens of the QueryRunner window don't
  // leak workers.
  useEffect(() => {
    let cancelled = false;
    const client = new SqliteClient();
    clientRef.current = client;
    client
      .init()
      .then((info) => {
        if (!cancelled) setStatus({ phase: 'ready', info });
      })
      .catch((err: Error) => {
        if (!cancelled) setStatus({ phase: 'error', message: err.message ?? String(err) });
      });
    return () => {
      cancelled = true;
      client.dispose();
      clientRef.current = null;
    };
  }, []);

  return { client: clientRef.current, status };
}
